import * as coreEntities from 'core/store/index';
import { CLIENT } from 'core/variables/constant';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useSystemSettings from './useSystemSettings';

const LEGACY_CALENDAR_CLIENTS = new Set([
  CLIENT.EDELVIVES,
  CLIENT.GOTITBYME,
  CLIENT.MASTER,
  CLIENT.MASTER_DEMO,
  CLIENT.MULTITUDE,
  CLIENT.TINTA_FRESCA,
  CLIENT.MACMILLAN,
  CLIENT.SANTILLANA_ALFA,
  CLIENT.BRUNO,
]);

/**
 * `enablePrograming` comes from useSystemSettings, whose effect is registered before the one in this
 * hook: on the commit where `client` lands its setState is already queued, but the previous render's
 * value (`undefined`) is still what gets read here. Publishing `false` in that gap made /calendar run
 * the advanced calendar load and seed `calendarsShows` with course guids, leaving the legacy calendar
 * empty. It now stays `undefined` until the system settings resolve: consumers already
 * wait on that state. Clients that are legacy regardless of the settings keep resolving immediately.
 */
function resolveIsLegacyCalendar(client, enablePrograming) {
  if (LEGACY_CALENDAR_CLIENTS.has(client)) return true;
  if (enablePrograming === undefined) return undefined;
  return enablePrograming === false;
}

export function useOrganization() {
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const tenantData = useSelector((state) => coreEntities.organization.selectors.getTenant(state));
  // const featureFlags = useSelector((state) => coreEntities.organization.selectors.getFeatureFlags(state));
  const canCreateGroup = useSelector((state) => coreEntities.organization.selectors.getCanCreateGroup(state));
  const canCreateCourse = useSelector((state) => coreEntities.organization.selectors.getCanCreateCourse(state));

  const { enableThirdPartyIntegrations, enablePrograming } = useSystemSettings();

  const [canChangeAvatar, setCanChangeAvatar] = useState(true);
  const [hasSupport, setHasSupport] = useState(true);
  const [showAccountTab, setShowAccountTab] = useState(true);
  const [showDisregardedPrograms, setShowDisregardedPrograms] = useState(true);
  const [showKidsMode, setShowKidsMode] = useState(CLIENT.SANTILLANA_PILOTO);
  const [canEditProfileInfo, setCanEditProfileInfo] = useState(true);
  const [canEditCourse, setCanEditCourse] = useState(true);
  const [logoutRedirect, setLogoutRedirect] = useState(client === CLIENT.EDELVIVES || client === CLIENT.SANTILLANA_ALFA);
  const [hideLibrary, setHideLibrary] = useState(false);
  const [hideTeacher, setHideTeacher] = useState(false);
  const [hideEducationLevelGroup, setHideEducationLevelGroup] = useState(false);
  const [showSelectTypeLesson, setShowSelectTypeLesson] = useState(false);
  const [showImmersiveReaderFlag, setShowImmersiveReaderFlag] = useState(false);
  // const [allowMoveCalendarSessions, setAllowMoveCalendarSessions] = useState(false);
  const [showPersonalizeAvatar, setShowPersonalizeAvatar] = useState(false);
  const [newProgramInterface, setNewProgramInterface] = useState(client !== CLIENT.TINTA_FRESCA && client !== CLIENT.LIBROS_Y_LIBROS);
  const [enableTeacherCanCreateSection, setEnableTeacherCanCreateSection] = useState(true);
  const [isLegacyCalendar, setIsLegacyCalendar] = useState(undefined);
  const [showMintStudentVersionFirst, setShowMintStudentVersionFirst] = useState(false);
  const [hasFivePeriodsBimonthly, setHasFivePeriodsBimonthly] = useState(false);

  useEffect(() => {
    if (client) {
      setShowKidsMode(client === CLIENT.SANTILLANA_PILOTO);
      setShowSelectTypeLesson(client === CLIENT.TANGERINE);
      setShowImmersiveReaderFlag(client === CLIENT.TANGERINE || client === CLIENT.GOTITBYME || client === CLIENT.EDELVIVES);
      setShowPersonalizeAvatar(client === CLIENT.TANGERINE || client === CLIENT.MASTER_DEMO || client === CLIENT.MULTITUDE);
      setEnableTeacherCanCreateSection(client !== CLIENT.SUMUM_CAMPUS);
      setIsLegacyCalendar(resolveIsLegacyCalendar(client, enablePrograming));
      setShowMintStudentVersionFirst(client === CLIENT.SUMUN_CAMPUS_BRASIL);
      setHasFivePeriodsBimonthly(client === CLIENT.SANTILLANA_PILOTO);

      // La V2 de la plantilla Standard (StandardView) se activa para todos los clientes menos Tinta Fresca
      // y Libros y Libros, que por decisión de producto siguen usando la plantilla Standard legacy por ahora.
      // Gotit no se excluye aquí: sus cursos son STEAM y el guard de plantilla de StandardCourseViewWrapper
      // (!isSteam) los mantiene siempre en el programa legacy (LegacyStandardView).
      setNewProgramInterface(client !== CLIENT.TINTA_FRESCA && client !== CLIENT.LIBROS_Y_LIBROS);
    }
  }, [client, enablePrograming]);

  useEffect(() => {
    if (enableThirdPartyIntegrations) {
      setCanChangeAvatar(false);
      setHasSupport(false);
      setShowAccountTab(false);
      setShowDisregardedPrograms(false);
      setCanEditProfileInfo(false);
      setCanEditCourse(false);
      setLogoutRedirect(true);
      setHideLibrary(true);
      setHideTeacher(true);
      setHideEducationLevelGroup(true);
      setNewProgramInterface(true);
    }
  }, [enableThirdPartyIntegrations]);

  return {
    client,
    tenantConfig: {
      ...tenantData,
      hideTeacher,
      showLogout: true,
      canChangeAvatar,
      hasSupport,
      showAccountTab,
      canCreateGroup,
      canCreateCourse,
      showDisregardedPrograms,
      showKidsMode,
      hideLibrary,
      hideEducationLevelGroup,
      canEditProfileInfo,
      canEditCourse,
      logoutRedirect,
      newProgramInterface,
      showSelectTypeLesson,
      showImmersiveReaderFlag,
      // allowMoveCalendarSessions,
      showPersonalizeAvatar,
      enableTeacherCanCreateSection,
      isLegacyCalendar,
      showMintStudentVersionFirst,
      hasFivePeriodsBimonthly,
    },
  };
}
